import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * Gemeinsamer Draht zwischen dem GBU-Editor und allem, was darin speichert
 * (Gefaehrdungskarte, Massnahmen- und Vorsorgezeile, Listenfelder).
 *
 *   melde(schluessel, offen)  — diese Stelle hat (nicht) gespeicherte Eingaben.
 *                               Gezaehlt wird nur wirklich Offenes: ausstehend
 *                               oder fehlgeschlagen.
 *   registriere(flush)        — „jetzt speichern und warten"; der Editor ruft
 *                               alle vor „Zur Freigabe", „Vorschau" und Zurueck.
 *
 * WARUM: eingefroren wird der GESPEICHERTE Stand. Ohne diesen Draht fehlte
 * in einer Fassung, was ein Nutzer Sekunden vorher getippt hat.
 */
export interface SpeicherKontextWert {
  melde: (schluessel: string, offen: boolean) => void;
  registriere: (flush: () => Promise<boolean>) => () => void;
}

export const SpeicherKontext = createContext<SpeicherKontextWert | null>(null);

/** Meldet `offen` unter einem eigenen Schluessel; beim Abbauen gilt die Stelle als erledigt. */
export const useUngespeichert = (offen: boolean): void => {
  const kontext = useContext(SpeicherKontext);
  const schluessel = useId();
  useEffect(() => {
    if (!kontext) return undefined;
    kontext.melde(schluessel, offen);
    return () => kontext.melde(schluessel, false);
  }, [kontext, schluessel, offen]);
};

/**
 * Eine Gruppe (z. B. eine Gefaehrdungskarte mit ihren Zeilen): reicht alles an
 * den Editor weiter UND kann ihre eigenen Mitglieder gesammelt speichern —
 * fuer „Zuklappen wartet auf ausstehende Speicherungen". `offen` zaehlt die
 * Mitglieder mit nicht gespeicherten Eingaben (Anzeige im Kartenkopf).
 */
export const useSpeicherGruppe = (): { wert: SpeicherKontextWert; flush: () => Promise<boolean>; offen: number } => {
  const eltern = useContext(SpeicherKontext);
  const mitglieder = useRef<Set<() => Promise<boolean>>>(new Set());
  const offeneRef = useRef<Set<string>>(new Set());
  const [offen, setOffen] = useState(0);
  const wert = useMemo<SpeicherKontextWert>(() => ({
    melde: (schluessel, istOffen) => {
      if (istOffen) offeneRef.current.add(schluessel); else offeneRef.current.delete(schluessel);
      setOffen(offeneRef.current.size);
      eltern?.melde(schluessel, istOffen);
    },
    registriere: (f) => {
      mitglieder.current.add(f);
      const abmelden = eltern?.registriere(f);
      return () => {
        mitglieder.current.delete(f);
        abmelden?.();
      };
    },
  }), [eltern]);
  const flush = useCallback(async () => (await Promise.all([...mitglieder.current].map((f) => f()))).every(Boolean), []);
  return { wert, flush, offen };
};
